// Topics (wire name "topic sets") — the Data structure tab's subject labels
// that group forms and inspections so reports can roll up by subject. A topic
// is just a title. Endpoints (user-confirmed 2026-09-14): POST /topic-sets/all
// is the usual paged body ({ page, limit, search? }, plus the Last-updated
// toolbar filter and an orderBy); POST /topic-sets/create
// and PATCH /topic-sets/update/:id share the `{ title }` body; POST
// /topic-sets/bulk-delete removes any number in one call (body { topicSetIds })
// — a one-row delete sends a one-element array.
//
// The list ROW shape is typed on the form-sets idiom ({ _id, title,
// lastUpdatedBy, createdAt, updatedAt } and totalRecords) — an assumption to
// verify against the first real response, the endpoints themselves are
// confirmed. File is named after the wire ("topic sets"); the exported types
// use the UI name ("topics"), the formSetsApi/OptionSet precedent.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { FormDateFilter } from './formsApi';

/** POST /topic-sets/create + PATCH /topic-sets/update/:id body. */
export interface TopicPayload {
  title: string;
}

/** Backend `orderBy.key` values — the confirmed pair; members double as the
 *  sortable columns' `key`s. */
export enum TopicSortKey {
  Title = 'title',
  UpdatedAt = 'updatedAt',
}

/** POST /topic-sets/all body. `search` is omitted when the box is empty; the
 *  Last-updated filter params are omitted when nothing is picked (the shared
 *  date-preset contract — "Any time" = send nothing). */
export interface TopicQueryArgs {
  page: number;
  limit: number;
  search?: string;
  lastUpdated?: FormDateFilter;
  /** yyyy-mm-dd; sent only with CUSTOM. */
  lastUpdatedStart?: string;
  lastUpdatedEnd?: string;
  orderBy?: { key: TopicSortKey; type: 'asc' | 'desc' };
}

export interface TopicPerson {
  id: string;
  fullName: string;
}

/** One list row. */
export interface Topic {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  lastUpdatedBy: TopicPerson | null;
}

/** `totalRecords` is normalized to `total`. */
/** One topic as the catalogue returns it — id and title, nothing else. Its own
 *  type rather than a Partial of Topic: this payload is complete for what it
 *  is, and optionals would make every consumer re-check fields that are always
 *  there. */
export interface TopicSelection {
  id: string;
  title: string;
}

export interface TopicPage {
  items: Topic[];
  total: number;
}

// ---- raw shapes ----
interface ApiTopicPerson {
  _id: string;
  user: { firstName: string; lastName: string };
}

interface ApiTopic {
  _id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  lastUpdatedBy: ApiTopicPerson | null;
}

interface ApiTopicPage {
  items?: ApiTopic[];
  totalRecords?: number;
}

const toPerson = (p: ApiTopicPerson | null): TopicPerson | null => {
  if (!p) return null;
  const fullName = `${p.user.firstName} ${p.user.lastName}`.trim();
  return fullName ? { id: p._id, fullName } : null;
};

const toTopic = (t: ApiTopic): Topic => ({
  id: t._id,
  title: t.title,
  createdAt: t.createdAt,
  updatedAt: t.updatedAt,
  lastUpdatedBy: toPerson(t.lastUpdatedBy),
});

export const topicSetsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTopics: build.query<TopicPage, TopicQueryArgs>({
      query: (body) => ({ ...ROUTES.topicSets.list, data: body }),
      transformResponse: (res: ApiTopicPage) => {
        const items = res?.items ?? [];
        return { items: items.map(toTopic), total: res?.totalRecords ?? items.length };
      },
      providesTags: [{ type: 'TopicSet', id: 'LIST' }],
    }),
    /** The topic catalogue. The Data setup module's Form structure tab needs it
     *  to resolve a row's topic TITLE back to an id, because the save sends
     *  ids and the row carries only the title.
     *
     *  Shares the LIST tag rather than taking a SELECTION id of its own: every
     *  mutation that changes the paged list changes this catalogue too, so
     *  sharing is what stops a future mutation forgetting to invalidate it —
     *  the form-set selection rule. */
    getTopicSelection: build.query<TopicSelection[], void>({
      query: () => ({ ...ROUTES.topicSets.selection }),
      transformResponse: (res: { _id: string; title: string }[]) =>
        (res ?? []).map((t) => ({ id: t._id, title: t.title })),
      providesTags: [{ type: 'TopicSet', id: 'LIST' }],
    }),
    createTopic: build.mutation<{ _id?: string }, TopicPayload>({
      query: (body) => ({ ...ROUTES.topicSets.create, data: body }),
      invalidatesTags: [{ type: 'TopicSet', id: 'LIST' }],
    }),
    updateTopic: build.mutation<{ _id?: string }, { id: string; body: TopicPayload }>({
      query: ({ id, body }) => ({ ...ROUTES.topicSets.update(id), data: body }),
      invalidatesTags: [{ type: 'TopicSet', id: 'LIST' }],
    }),
    // Takes the ids directly and wraps them in the body's `topicSetIds` key
    // here, so no call site has to know the envelope.
    bulkDeleteTopics: build.mutation<unknown, string[]>({
      query: (topicSetIds) => ({ ...ROUTES.topicSets.bulkDelete, data: { topicSetIds } }),
      invalidatesTags: [{ type: 'TopicSet', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetTopicsQuery,
  useGetTopicSelectionQuery,
  useCreateTopicMutation,
  useUpdateTopicMutation,
  useBulkDeleteTopicsMutation,
} = topicSetsApi;
